import { Accordion } from '@codecorp/components'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { SettingsListContent } from './settings-list-content'
import {
    ProductDto,
    useMcodesGetCategorySettingsQuery,
} from '@codecorp/web-api-wrapper'

export interface SettingsListProps {
    category: string
    selectedDevice: ProductDto
    searchTerm: string
    isFirstCategory: boolean
}

export const SettingsList = ({
    category,
    selectedDevice,
    searchTerm,
    isFirstCategory,
}: SettingsListProps) => {
    const { data: allSettings = [] } = useMcodesGetCategorySettingsQuery({
        deviceName: selectedDevice.name,
        category: category,
    })
    // This initializes the first accordion, then based on the searchTerm it will open the accordion if there are matches
    const [accordionValue, setAccordionValue] = useState<string[]>(() => {
        if (!searchTerm.trim()) {
            return isFirstCategory ? [category] : []
        }

        const hasMatch = allSettings.some(
            (setting) =>
                setting.code.toLowerCase().includes(searchTerm) ||
                setting.description.toLowerCase().includes(searchTerm),
        )

        return hasMatch ? [category] : []
    })
    //Reactively updates the open tabs when the search changes.
    useEffect(() => {
        if (!searchTerm.trim()) {
            setAccordionValue(isFirstCategory ? [category] : [])
            return
        }

        const hasMatch = allSettings.some(
            (setting) =>
                setting.code.toLowerCase().includes(searchTerm) ||
                setting.description.toLowerCase().includes(searchTerm),
        )

        setAccordionValue(hasMatch ? [category] : [])
    }, [searchTerm, allSettings, isFirstCategory, category])

    const filteredSettings = allSettings.filter(
        (setting) =>
            setting.code.toLowerCase().includes(searchTerm) ||
            setting.description.toLowerCase().includes(searchTerm),
    )

    const listItems = useMemo(() => {
        const items: Record<string, ReactNode> = {}
        items[category] = <SettingsListContent settings={filteredSettings} />
        return items
    }, [category, filteredSettings])

    //This is set to null if the category does NOT include the search term
    if (searchTerm.trim() && filteredSettings.length === 0) {
        return null
    }

    return (
        <Accordion
            className="-mx-4 mb-4 max-w-[unset] sm:mx-0 sm:max-w-full"
            type="multiple"
            items={listItems}
            value={accordionValue}
            onValueChange={(value) => setAccordionValue(value)}
        />
    )
}
